import { Currency, Ether, NativeCurrency, Token, WETH9 } from '@uniswap/sdk-core'
import { SupportedChainId } from 'constants/chains'
import invariant from 'tiny-invariant'

import arbTokens from './networks/arbitrum'
import fantomTokens from './networks/fantom'
import zkTokens from './networks/zk-sync'

export const WRAPPED_NATIVE_CURRENCY: { [chainId: number]: Token | undefined } = {
  ...(WETH9 as Record<SupportedChainId, Token>),
  [SupportedChainId.ZK_SYNC]: zkTokens.weth,
  [SupportedChainId.FANTOM]: fantomTokens.wftm,
  [SupportedChainId.ARBITRUM]: arbTokens.weth,
}

class ExtendedEther extends Ether {
  public get wrapped(): Token {
    const wrapped = WRAPPED_NATIVE_CURRENCY[this.chainId]
    if (wrapped) return wrapped
    throw new Error(`Unsupported chain ID: ${this.chainId}`)
  }

  private static _cachedExtendedEther: { [chainId: number]: NativeCurrency } = {}

  public static onChain(chainId: number): ExtendedEther {
    return this._cachedExtendedEther[chainId] ?? (this._cachedExtendedEther[chainId] = new ExtendedEther(chainId))
  }
}

function isFtm(chainId: number): chainId is SupportedChainId.FANTOM {
  return chainId === SupportedChainId.FANTOM
}

class FtmNativeCurrency extends NativeCurrency {
  equals(other: Currency): boolean {
    return other.isNative && other.chainId === this.chainId
  }

  get wrapped(): Token {
    if (!isFtm(this.chainId)) throw new Error('Not ftm')
    const wrapped = WRAPPED_NATIVE_CURRENCY[this.chainId]
    invariant(wrapped instanceof Token)
    return wrapped
  }

  public constructor(chainId: number) {
    if (!isFtm(chainId)) throw new Error('Not ftm')
    super(chainId, 18, 'FTM', 'FTM')
  }
}

const cachedNativeCurrency: { [chainId: number]: NativeCurrency | Token } = {}
export function nativeOnChain(chainId: number): NativeCurrency | Token {
  if (cachedNativeCurrency[chainId]) return cachedNativeCurrency[chainId]
  let nativeCurrency: NativeCurrency | Token
  if (isFtm(chainId)) {
    nativeCurrency = new FtmNativeCurrency(chainId)
  } else {
    nativeCurrency = ExtendedEther.onChain(chainId)
  }
  return (cachedNativeCurrency[chainId] = nativeCurrency)
}

export const TOKEN_SHORTHANDS: { [shorthand: string]: { [chainId in SupportedChainId]?: string } } = {
  USDC: {
    // [SupportedChainId.MAINNET]: USDC_MAINNET.address,
    [SupportedChainId.ZK_SYNC]: zkTokens.usdc.address,
    [SupportedChainId.FANTOM]: fantomTokens.usdc.address,
    [SupportedChainId.ARBITRUM]: arbTokens.usdc.address,
  },
}

export const SWAP_DEFAULT_TOKEN: { [chainId: number]: Token | undefined } = {
  [SupportedChainId.ZK_SYNC]: zkTokens.usdc,
  [SupportedChainId.FANTOM]: fantomTokens.usdc,
  [SupportedChainId.ARBITRUM]: arbTokens.usdc,
}

export const WAGMI_TOKEN: { [chainId: number]: Token } = {
  [SupportedChainId.ARBITRUM]: arbTokens.wagmi,
}
